import { OpenRouterModelType } from "@aix/apiutils/src/llms/enums"
import React, { useEffect, useRef, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"

import { trpc } from "../lib/trpc"

interface BrainChatboxProps {
  selectedProjectId?: string | null
}

const CHAT_MODES = {
  creation: {
    name: "Creation",
    description: "Create and find existing tasks, merge them together with project suggestions",
  },
  prioritization: {
    name: "Prioritization",
    description: "Analyze and reorder task priorities with proposed changes",
  },
  execution: {
    name: "Execution",
    description: "Execute tasks and track progress (Coming Soon)",
  },
} as const

type ChatMode = keyof typeof CHAT_MODES

const BrainChatbox: React.FC<BrainChatboxProps> = ({ selectedProjectId }) => {
  const [message, setMessage] = useState("")
  const [chatMode, setChatMode] = useState<ChatMode>("creation")
  const [showCreationStaging, setShowCreationStaging] = useState(false)
  const [showPrioritizationStaging, setShowPrioritizationStaging] = useState(false)
  const [stagedTasks, setStagedTasks] = useState<any[]>([])
  const [priorityChanges, setPriorityChanges] = useState<any[]>([])
  const [selectedModel] = useState<OpenRouterModelType>(OpenRouterModelType.DEEPSEEK_R1_DISTILL_LLAMA_70B)
  const [isExpanded, setIsExpanded] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // tRPC mutations
  const stageTasks = trpc.brain.stageTasks.useMutation({
    onSuccess: (result) => {
      console.log("Staged tasks received:", result.stagedTasks)
      setStagedTasks(result.stagedTasks || [])
      setShowCreationStaging(true)
    },
    onError: (error) => {
      console.error("Failed to stage tasks:", error)
      alert(`Failed to stage tasks: ${error.message}`)
    },
  })

  const utils = trpc.useUtils()

  const createStagedTasks = trpc.brain.createStagedTasks.useMutation({
    onMutate: async (variables) => {
      // Get affected project IDs for optimistic updates
      const affectedProjectIds = new Set<string>()

      // Add selected project if exists
      if (selectedProjectId) {
        affectedProjectIds.add(selectedProjectId)
      }

      // Add project IDs from tasks that will be created
      if (variables && variables.tasks) {
        variables.tasks.forEach((task) => {
          if (task.projectId && task.action === "create") {
            affectedProjectIds.add(task.projectId)
          }
        })
      }

      // Cancel outgoing refetches for all affected projects
      const cancellations = Array.from(affectedProjectIds).map((projectId) =>
        utils.brain.getProjectTasks.cancel({ projectId }),
      )
      await Promise.all(cancellations)

      // Snapshot previous values for rollback
      const previousData: Record<string, any> = {}

      // Apply optimistic updates to each affected project
      for (const projectId of affectedProjectIds) {
        const previousTasks = utils.brain.getProjectTasks.getData({ projectId })
        previousData[projectId] = previousTasks

        if (previousTasks) {
          const optimisticTasks = (variables && variables.tasks ? variables.tasks : [])
            .filter(
              (task) =>
                task.action === "create" &&
                (task.projectId === projectId || (!task.projectId && projectId === selectedProjectId)),
            )
            .map((task, index) => ({
              id: `optimistic-${Date.now()}-${index}`,
              content: task.content,
              description: task.description || null,
              projectId: task.projectId || projectId,
              assigneeId: null,
              status: "todo" as const,
              priority: task.priority || 0,
              estimatedMinutes: task.estimatedMinutes || null,
              alignmentScore: 5, // Default alignment score
              alignmentReasoning: "Pending calculation",
              aiCategorized: !task.projectId, // True if auto-categorized
              isWeeklyPriority: false,
              weekAssigned: null,
              subtasks: null,
              createdAt: new Date(),
              updatedAt: new Date(),
              projectTitle: "",
              projectDescription: null,
              assigneeName: null,
              assigneeEmail: null,
              assigneeAvatarUrl: null,
              labels: [],
            }))

          if (optimisticTasks.length > 0) {
            utils.brain.getProjectTasks.setData(
              { projectId },
              {
                tasks: [...previousTasks.tasks, ...optimisticTasks],
              },
            )
          }
        }
      }

      return { previousData, affectedProjectIds: Array.from(affectedProjectIds) }
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousData && context?.affectedProjectIds) {
        context.affectedProjectIds.forEach((projectId) => {
          const previousTasks = context.previousData[projectId]
          if (previousTasks) {
            utils.brain.getProjectTasks.setData({ projectId }, previousTasks)
          }
        })
      }
      console.error(`Failed to create tasks: ${err.message}`)
    },
    onSuccess: (result, variables, context) => {
      setShowCreationStaging(false)
      setStagedTasks([])
      setMessage("")
      console.log(`Successfully processed ${result.processedCount} tasks`)

      // Invalidate and refetch all affected projects to get real data from server
      if (context?.affectedProjectIds) {
        context.affectedProjectIds.forEach((projectId) => {
          void utils.brain.getProjectTasks.invalidate({ projectId })
        })
      }

      // Also invalidate other related queries that might show tasks
      void utils.brain.getProjectHierarchy.invalidate()
      void utils.brain.searchContent.invalidate()
      void utils.brain.getWeeklyTasks.invalidate() // For weekly view
    },
    onSettled: (data, error, variables, context) => {
      // Always refetch after error or success to ensure we have fresh data
      if (context?.affectedProjectIds) {
        context.affectedProjectIds.forEach((projectId) => {
          void utils.brain.getProjectTasks.invalidate({ projectId })
        })
      }
    },
  })

  const chatWithAI = trpc.brain.chatWithAI.useMutation({
    onSuccess: (result) => {
      if (result.mode === "prioritization" && result.createdItems?.length > 0) {
        setPriorityChanges(result.createdItems)
        setShowPrioritizationStaging(true)
      }
      setMessage("")
    },
    onError: (error) => {
      alert(`AI Chat failed: ${error.message}`)
    },
  })

  const handleSubmit = async () => {
    if (!message.trim()) return

    if (chatMode === "creation") {
      await stageTasks.mutateAsync({
        message: message.trim(),
        projectId: selectedProjectId || undefined,
        model: selectedModel,
      })
    } else if (chatMode === "prioritization") {
      await chatWithAI.mutateAsync({
        message: message.trim(),
        mode: chatMode,
        projectId: selectedProjectId || undefined,
        model: selectedModel,
      })
    } else if (chatMode === "execution") {
      alert("Execution mode coming soon!")
    }
  }

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"
      textareaRef.current.style.height = `${Math.max(44, textareaRef.current.scrollHeight)}px`
    }
  }, [message])

  // Use hotkeys for CMD+Enter
  useHotkeys("cmd+enter,ctrl+enter", () => {
    void handleSubmit()
  }, [message, chatMode, selectedProjectId])

  const isLoading = stageTasks.isLoading || createStagedTasks.isLoading || chatWithAI.isLoading

  const handleFocus = () => {
    setIsExpanded(true)
  }

  const handleBlur = () => {
    if (!message.trim()) {
      setIsExpanded(false)
    }
  }

  return (
    <>
      {/* Centered Chat Box */}
      <div className="fixed bottom-0 left-0 right-0 z-50 flex justify-center">
        <div
          className={`bg-background border border-gray-300 rounded-t-lg shadow-lg transition-all duration-200 ${
            isExpanded ? "w-[80%] max-w-4xl" : "w-[60%] max-w-2xl"
          }`}
        >
          {/* Mode Selection */}
          {isExpanded && (
            <div className="border-b border-border p-4">
              <div className="flex gap-2 justify-center">
                {Object.entries(CHAT_MODES).map(([key, mode]) => (
                  <button
                    key={key}
                    onClick={() => setChatMode(key as ChatMode)}
                    className={`px-4 py-2 text-sm rounded-md transition-colors ${
                      chatMode === key ? "bg-blue-100 text-blue-700 border border-blue-300" : "bg-foregroundx  "
                    }`}
                    disabled={key === "execution"}
                  >
                    {mode.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="p-4">
            <div className="flex gap-3">
              <div className="flex-1">
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  placeholder={
                    isExpanded
                      ? `${CHAT_MODES[chatMode].description} (⌘+Enter to send)`
                      : "Ask Brain to create tasks..."
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none overflow-hidden"
                  disabled={isLoading}
                  rows={1}
                  style={{ minHeight: "48px" }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault()
                      void handleSubmit()
                    }
                  }}
                />
              </div>

              <button
                onClick={handleSubmit}
                disabled={!message.trim() || isLoading}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors flex items-center justify-center"
                style={{ minHeight: "48px" }}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <svg
                      className="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {isExpanded && <span>Processing...</span>}
                  </div>
                ) : (
                  <span>{isExpanded ? "Send" : "→"}</span>
                )}
              </button>
            </div>

            {/* Status Indicator */}
            {isExpanded && (
              <div className="flex items-center justify-between mt-3 text-sm text-gray-500">
                <div>{selectedProjectId ? `${CHAT_MODES[chatMode].name} mode` : "Global mode"}</div>
                <div className="text-xs text-gray-400">⌘+Enter to send quickly</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Creation Staging Modal */}
      {showCreationStaging && (
        <CreationStagingModal
          stagedTasks={stagedTasks}
          onClose={() => {
            setShowCreationStaging(false)
            setStagedTasks([])
          }}
          onConfirm={(tasks) => {
            createStagedTasks.mutate({ tasks })
          }}
          onIterate={(refinedTasks) => {
            setStagedTasks(refinedTasks)
          }}
          isLoading={createStagedTasks.isLoading}
          selectedProjectId={selectedProjectId}
        />
      )}

      {/* Prioritization Staging Modal */}
      {showPrioritizationStaging && (
        <PrioritizationStagingModal
          changes={priorityChanges}
          onClose={() => {
            setShowPrioritizationStaging(false)
            setPriorityChanges([])
          }}
          onConfirm={() => {
            // TODO: Implement priority changes
            alert("Priority changes will be implemented soon!")
            setShowPrioritizationStaging(false)
            setPriorityChanges([])
          }}
          isLoading={false}
        />
      )}
    </>
  )
}

// Creation Staging Modal Component
interface CreationStagingModalProps {
  stagedTasks: {
    id: string
    content: string
    description?: string | null
    projectId?: string | null
    priority?: number
    estimatedMinutes?: number | null
    action?: "create" | "merge"
    existingTaskId?: string | null
  }[]
  onClose: () => void
  onConfirm: (
    tasks: {
      content: string
      description?: string
      projectId?: string
      priority?: number
      estimatedMinutes?: number | null
      action?: "create" | "merge"
      existingTaskId?: string
    }[],
  ) => void
  onIterate?: (
    tasks: {
      id: string
      content: string
      description: string
      projectId: string | null
      action: "create" | "merge"
      existingTaskId: string | null
    }[],
  ) => void
  isLoading: boolean
  selectedProjectId?: string | null
}

const CreationStagingModal: React.FC<CreationStagingModalProps> = ({
  stagedTasks,
  onClose,
  onConfirm,
  onIterate,
  isLoading,
  selectedProjectId,
}) => {
  console.log("CreationStagingModal received tasks:", stagedTasks)
  const [editedTasks, setEditedTasks] = useState(
    stagedTasks.map((task) => ({
      ...task,
      priority: task.priority ?? 0, // Default to lowest priority
      estimatedMinutes: task.estimatedMinutes ?? 30, // Default to 30 minutes
      action: task.action ?? "create", // Default to create
      existingTaskId: task.existingTaskId ?? null,
    })),
  )
  const [refinementMessage, setRefinementMessage] = useState("")
  const [showIterateInput, setShowIterateInput] = useState(false)
  const { data: allProjectsData } = trpc.brain.getAllProjects.useQuery()

  const iterateStagedTasks = trpc.brain.iterateStagedTasks.useMutation({
    onSuccess: (result) => {
      console.log("Iterated tasks received:", result.stagedTasks)
      // Update the edited tasks with the refined tasks
      setEditedTasks(
        result.stagedTasks.map((task) => ({
          ...task,
          priority: task.priority ?? 0,
          estimatedMinutes: task.estimatedMinutes ?? 30,
          description: task.description || null,
        })),
      )
      setRefinementMessage("")
      setShowIterateInput(false)
      // Show success message with changes
      if (result.changes && result.changes.length > 0) {
        console.log("Changes made:", result.changes)
      }
    },
    onError: (error) => {
      console.error("Failed to iterate tasks:", error)
      alert(`Failed to iterate tasks: ${error.message}`)
    },
  })

  const updateTask = (index: number, field: string, value: any) => {
    const updated = [...editedTasks]
    const currentTask = updated[index]
    if (currentTask && currentTask.id && currentTask.content) {
      updated[index] = { ...currentTask, [field]: value }
      setEditedTasks(updated)
    }
  }

  const removeTask = (index: number) => {
    setEditedTasks(editedTasks.filter((_, i) => i !== index))
  }

  const handleIterate = () => {
    if (!refinementMessage.trim()) return

    const tasksToIterate = editedTasks.map((task) => ({
      id: task.id,
      content: task.content,
      description: task.description || task.content,
      projectId: task.projectId || null,
      action: task.action,
      existingTaskId: task.existingTaskId,
    }))

    iterateStagedTasks.mutate({
      stagedTasks: tasksToIterate,
      refinementMessage: refinementMessage.trim(),
      projectId: selectedProjectId || undefined,
      model: OpenRouterModelType.DEEPSEEK_R1_DISTILL_LLAMA_70B,
    })
  }

  const handleConfirm = () => {
    const tasksToCreate = editedTasks.map((task) => ({
      content: task.content,
      description: task.description || undefined,
      projectId: task.projectId || undefined,
      priority: task.priority,
      estimatedMinutes: task.estimatedMinutes,
      action: task.action,
      existingTaskId: task.existingTaskId ?? undefined,
    }))
    onConfirm(tasksToCreate)
  }

  const getPriorityColor = (priority: number) => {
    if (priority >= 4) return "text-destructive bg-red-50 border-red-200"
    if (priority >= 3) return "text-orange-600 bg-orange-50 border-orange-200"
    if (priority >= 2) return "text-yellow-600 bg-yellow-50 border-yellow-200"
    if (priority >= 1) return " bg-blue-50 border-blue-200"
    return " bg-foregroundx border-border"
  }

  const formatEstimatedTime = (minutes?: number | null) => {
    if (!minutes) return ""
    if (minutes < 60) return `${minutes}m`
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold">Review & Create Tasks</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-primary">
            ✕
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {editedTasks.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No tasks were extracted from your message.</p>
              <p className="text-sm mt-2">Try describing specific tasks you want to create.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {editedTasks.map((task, index) => (
                <div key={task.id} className="border border-border rounded-lg p-3">
                  <div className="space-y-3">
                    {/* Task Action Badge */}
                    <div className="flex items-center gap-2 mb-2">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          task.action === "merge" ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"
                        }`}
                      >
                        {task.action === "merge" ? "🔄 MERGE" : "✨ CREATE"}
                      </span>

                      {task.action === "merge" && task.existingTaskId && (
                        <div className="flex flex-col gap-1">
                          <span className="text-xs ">→ Merging into existing task:</span>
                          <span className="text-xs  font-medium">ID: {task.existingTaskId.slice(-8)}</span>
                        </div>
                      )}
                    </div>

                    {/* Task Title */}
                    <div>
                      <input
                        type="text"
                        value={task.content}
                        onChange={(e) => updateTask(index, "content", e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded text-sm font-medium"
                        placeholder={
                          task.action === "merge" ? "Additional information to add..." : "Enter task title..."
                        }
                      />
                    </div>

                    {/* Compact row for Project, Priority, Time, and Actions */}
                    <div className="flex items-center gap-3 flex-wrap">
                      {/* Project */}
                      <div className="flex-1 min-w-[200px]">
                        <select
                          value={task.projectId || ""}
                          onChange={(e) => updateTask(index, "projectId", e.target.value || null)}
                          className="w-full p-1.5 text-xs border border-gray-300 rounded"
                        >
                          <option value="">📁 Select Project...</option>
                          {allProjectsData?.projects?.map((project) => (
                            <option key={project.id} value={project.id}>
                              {project.displayName}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Priority */}
                      <div className="min-w-[120px]">
                        <select
                          value={task.priority}
                          onChange={(e) => updateTask(index, "priority", parseInt(e.target.value))}
                          className={`w-full p-1.5 text-xs border rounded ${getPriorityColor(task.priority)}`}
                        >
                          <option value={0}>P0 - Lowest</option>
                          <option value={1}>P1 - Low</option>
                          <option value={2}>P2 - Medium</option>
                          <option value={3}>P3 - High</option>
                          <option value={4}>P4 - Urgent</option>
                        </select>
                      </div>

                      {/* Estimated Time */}
                      <div className="min-w-[100px]">
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            value={task.estimatedMinutes || ""}
                            onChange={(e) =>
                              updateTask(index, "estimatedMinutes", e.target.value ? parseInt(e.target.value) : null)
                            }
                            className="w-16 p-1.5 text-xs border border-gray-300 rounded"
                            placeholder="30"
                            min="0"
                            step="15"
                          />
                          <span className="text-xs text-gray-500">min</span>
                          {task.estimatedMinutes && (
                            <span className="text-xs text-purple-600 font-medium">
                              ({formatEstimatedTime(task.estimatedMinutes)})
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Remove Button */}
                      <button
                        onClick={() => removeTask(index)}
                        className="text-destructive hover:text-destructive text-xs px-2 py-1 hover:bg-red-50 rounded"
                        title="Remove task"
                      >
                        ✕
                      </button>
                    </div>

                    {/* Description (Optional, Collapsible) */}
                    <div>
                      <textarea
                        value={task.description || ""}
                        onChange={(e) => updateTask(index, "description", e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded text-xs"
                        rows={2}
                        placeholder="Optional description..."
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Iteration Section */}
        {showIterateInput && (
          <div className="border-t border-border p-4 bg-foregroundx">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-primary">🔄 Refine Tasks</span>
                <button
                  onClick={() => {
                    setShowIterateInput(false)
                    setRefinementMessage("")
                  }}
                  className="text-gray-400 hover: text-xs"
                >
                  ✕
                </button>
              </div>
              <textarea
                value={refinementMessage}
                onChange={(e) => setRefinementMessage(e.target.value)}
                placeholder="Describe how you'd like to refine these tasks... (e.g., 'Add more detail to the descriptions', 'Split the large tasks into smaller ones', 'Make them more specific')"
                className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-none"
                rows={3}
                disabled={iterateStagedTasks.isLoading}
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowIterateInput(false)
                    setRefinementMessage("")
                  }}
                  className="px-3 py-1.5 text-sm  hover:text-gray-800"
                  disabled={iterateStagedTasks.isLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleIterate}
                  disabled={!refinementMessage.trim() || iterateStagedTasks.isLoading}
                  className="px-4 py-1.5 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {iterateStagedTasks.isLoading ? (
                    <>
                      <svg
                        className="animate-spin h-3 w-3"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Refining...
                    </>
                  ) : (
                    "Refine Tasks"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between p-4 border-t border-border">
          <div className="text-sm ">
            {editedTasks.length} task{editedTasks.length !== 1 ? "s" : ""} ready to create
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2  hover:text-gray-800">
              Cancel
            </button>
            {!showIterateInput && onIterate && (
              <button
                onClick={() => setShowIterateInput(true)}
                disabled={isLoading || iterateStagedTasks.isLoading || editedTasks.length === 0}
                className="px-4 py-2 text-purple-600 border border-purple-600 rounded hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                🔄 Refine
              </button>
            )}
            <button
              onClick={handleConfirm}
              disabled={isLoading || iterateStagedTasks.isLoading || editedTasks.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? "Creating..." : `Create ${editedTasks.length} Task${editedTasks.length !== 1 ? "s" : ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Prioritization Staging Modal Component
interface PrioritizationStagingModalProps {
  changes: any[]
  onClose: () => void
  onConfirm: () => void
  isLoading: boolean
}

const PrioritizationStagingModal: React.FC<PrioritizationStagingModalProps> = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold">Priority Changes</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-primary">
            ✕
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[50vh]">
          <div className="text-center py-8">
            <div className="text-6xl mb-4">🔄</div>
            <h3 className="text-lg font-semibold mb-2">Priority Changes Coming Soon</h3>
            <p className="">
              This feature will show proposed priority changes in JSON format and allow you to review and apply them.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end p-6 border-t border-border">
          <button onClick={onClose} className="px-4 py-2  hover:text-gray-800">
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default BrainChatbox
